import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Doctor, Patient } from "../contexts/types";

interface ConsentFormPdfTemplateProps {
  patient: Patient;
  doctor: Doctor;
  diagnosis: string;
  surgicalPlan: string;
  location: string;
  formattedDate: string;
}

const ConsentFormPdfTemplate: React.FC<ConsentFormPdfTemplateProps> = ({
  patient,
  doctor,
  diagnosis,
  surgicalPlan,
  location,
  formattedDate,
}) => {
  const { t } = useLanguage();
  const patientAge = patient.age ? t('yearsOld', { count: patient.age }) : t('bureaucracyNotInformedFemale');
  const patientSex = patient.sex === 'M' ? t('male') : t('female');
  const patientComorbidities = patient.comorbidities?.join(', ') || t('bureaucracyNone');
  const patientAllergies = patient.allergies?.join(', ') || t('bureaucracyNone');

  return (
    <div className="mx-auto max-w-4xl border bg-white p-10 font-sans text-gray-800 shadow-lg">
      <div className="mb-6 flex items-start justify-between border-b-2 border-blue-600 pb-4">
        <div className="flex items-center">
          <div className="mr-4 flex h-14 w-14 items-center justify-center rounded-lg bg-blue-600 text-white">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-8 w-8">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 10.5h3.75M9 15h3.75M12 18.75h3.75M12 7.5h3.75M6 12H3.75m3.75-1.5H6m3.75 3H6m3.75 1.5H6M18 12h3.75m-3.75-1.5H18m3.75 3H18m3.75 1.5H18M12 3v18" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-blue-900">OrthoPrecision</h1>
            <p className="text-xs font-semibold uppercase tracking-widest text-blue-600">{t('consentFormDigitalTitle')}</p>
          </div>
        </div>
        <div className="text-right text-xs">
          <p className="text-sm font-bold text-gray-900">Dr(a). {doctor.name}</p>
          <p className="text-gray-500">CRM {doctor.crm} {doctor.rqe ? `| RQE ${doctor.rqe}` : ''}</p>
          <p className="text-gray-500">{location}, {formattedDate}</p>
        </div>
      </div>

      <div className="mb-8 text-center">
        <h2 className="text-lg font-bold uppercase text-gray-700">
          {t('consentFormTitleLine1')} <br />
          <span className="text-blue-600">{t('consentFormTitleLine2')}</span>
        </h2>
      </div>

      <div className="mb-6 overflow-hidden rounded-t-lg border border-gray-200">
        <div className="flex items-center bg-blue-500 px-4 py-2">
          <span className="text-sm font-bold uppercase tracking-wide text-white">{t('consentFormPatientDataSection')}</span>
        </div>
        <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4">
          <div className="col-span-2">
            <label className="block text-[10px] font-bold uppercase text-gray-400">{t('patientPatientLabel')}</label>
            <p className="text-sm font-semibold">{patient.name}</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-gray-400">CPF</label>
            <p className="text-sm font-semibold">{patient.cpf || '---'}</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-gray-400">{t('agePlaceholder')}</label>
            <p className="text-sm font-semibold">{patientAge}</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-gray-400">{t('consentFormSexLabel')}</label>
            <p className="text-sm font-semibold">{patientSex}</p>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-gray-400">{t('comorbidities')}</label>
            <p className="text-sm font-semibold text-red-600">{patientComorbidities}</p>
          </div>
        </div>
      </div>

      <div className="mb-6 px-2 text-justify text-[11px] leading-relaxed text-gray-600">
        <p>{t('consentFormDeclaration')}</p>
      </div>

      <div className="mb-8 grid grid-cols-2 gap-4">
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="bg-gray-500 px-3 py-1.5">
            <span className="text-[10px] font-bold uppercase text-white">{t('consentFormProcedureSection')}</span>
          </div>
          <div className="min-h-[80px] p-3 text-xs font-medium">
            {surgicalPlan}
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <div className="bg-gray-500 px-3 py-1.5">
            <span className="text-[10px] font-bold uppercase text-white">{t('consentFormObservationsSection')}</span>
          </div>
          <div className="min-h-[80px] p-3 text-[10px] text-gray-500">
            <strong>{t('consentFormDiagnosisLabel')}:</strong> {diagnosis} <br />
            <strong>{t('consentFormAllergiesLabel')}:</strong> {patientAllergies}
          </div>
        </div>
      </div>

      <div className="mt-16 grid grid-cols-2 gap-12">
        <div className="text-center">
          <div className="border-t border-gray-400 pt-2">
            <p className="text-[10px] font-bold uppercase text-gray-700">{t('consentFormPatientOrGuardianSignature')}</p>
          </div>
        </div>
        <div className="text-center">
          <div className="border-t border-gray-400 pt-2 text-xs">
            <p className="font-bold text-gray-800">Dr(a). {doctor.name}</p>
            <p className="text-[9px] italic text-gray-500">CRM {doctor.crm} {doctor.rqe ? `| RQE ${doctor.rqe}` : ''}</p>
            <p className="mt-1 text-[8px] font-bold uppercase text-blue-700">{t('patientTechnicalResponsibility')}</p>
          </div>
        </div>
      </div>

      <div className="mt-8 border-t pt-4 text-center text-[9px] italic text-gray-400">
        {t('consentFormDigitalCopyNotice')}
      </div>
    </div>
  );
};

export default ConsentFormPdfTemplate;
